// components/booking/BookingSummary.js
import React from 'react';
import { CalendarDays, Clock, Pencil, Car } from 'lucide-react';

const BookingSummary = ({ 
  serviceDetails, 
  schedulingDetails, 
  vehicleDetails,
  showServiceChange = false,
  showScheduleChange = false,
  showVehicleChange = false,
  onChangeService,
  onChangeSchedule,
  onChangeVehicle
}) => {
  if (!serviceDetails) return null;
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Not selected';
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };
  
  // Get formatted vehicle name if available
  const getVehicleDisplay = () => {
    if (!vehicleDetails?.vehicle) return null;
    
    const { make, model, year } = vehicleDetails.vehicle;
    if (!make || !model) return null;
    
    return `${year} ${make} ${model}`;
  };
  
  // Get display price (adjusted or base)
  const getDisplayPrice = () => {
    return serviceDetails.adjustedPrice || serviceDetails.price;
  };
  
  // Return null if there's nothing to show
  if (!serviceDetails && !schedulingDetails?.date && !vehicleDetails?.vehicle) {
    return null;
  }
  
  return (
    <div className="bg-blue-50 p-4 rounded-lg mb-6">
      <div className="flex flex-col space-y-3">
        {/* Service Info */}
        {serviceDetails && (
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <p className="font-medium text-gray-700">{serviceDetails.title}</p>
              <p className="text-sm text-gray-500">${getDisplayPrice()}</p>
            </div>
            {showServiceChange && onChangeService && (
              <button 
                className="text-blue-600 flex items-center text-sm hover:text-blue-800"
                onClick={onChangeService}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Change
              </button>
            )}
          </div>
        )}
        
        {/* Scheduling Info */}
        {schedulingDetails?.date && (
          <div className="flex justify-between items-center">
            <div className="flex items-start">
              <CalendarDays className="h-4 w-4 text-blue-500 mr-2 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-gray-700">{formatDate(schedulingDetails.date)}</p>
                {schedulingDetails.time && (
                  <div className="flex items-center mt-1">
                    <Clock className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-500">{schedulingDetails.time}</p>
                  </div>
                )}
              </div>
            </div>
            {showScheduleChange && onChangeSchedule && (
              <button 
                className="text-blue-600 flex items-center text-sm hover:text-blue-800"
                onClick={onChangeSchedule}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Change
              </button>
            )}
          </div>
        )}
        
        {/* Vehicle Info (if available) */}
        {vehicleDetails?.vehicle && getVehicleDisplay() && (
          <div className="flex justify-between items-center">
            <div className="flex items-start">
              <Car className="h-4 w-4 text-blue-500 mr-2 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-gray-700">{getVehicleDisplay()}</p>
                {vehicleDetails.location && (
                  <p className="text-sm text-gray-500 truncate max-w-xs">
                    {vehicleDetails.location}
                  </p>
                )}
              </div>
            </div>
            {showVehicleChange && onChangeVehicle && (
              <button 
                className="text-blue-600 flex items-center text-sm hover:text-blue-800"
                onClick={onChangeVehicle}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Change
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingSummary;